import React, { useState, useEffect } from 'react';
import { X, Save, User } from 'lucide-react';

const ArtSchoolTeacherModal = ({ teacher, onClose, onSave }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', notes: '', isActive: 1
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (teacher) {
      setForm({
        firstName: teacher.FirstName || '',
        lastName:  teacher.LastName  || '',
        phone:     teacher.Phone     || '',
        email:     teacher.Email     || '',
        notes:     teacher.Notes     || '',
        isActive:  teacher.IsActive  ?? 1
      });
    }
  }, [teacher]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <User size={20} className="text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">
              {teacher ? 'Edit Teacher' : 'New Teacher'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={form.firstName}
                onChange={e => set('firstName', e.target.value)}
                placeholder="First name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={form.lastName}
                onChange={e => set('lastName', e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={form.phone}
              onChange={e => set('phone', e.target.value)}
              placeholder="+961 xx xxx xxx"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="teacher@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={2}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Any notes..."
            />
          </div>

          {teacher && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="teacherActive"
                checked={form.isActive === 1}
                onChange={e => set('isActive', e.target.checked ? 1 : 0)}
                className="w-4 h-4 rounded text-purple-600"
              />
              <label htmlFor="teacherActive" className="text-sm text-gray-700">Active</label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.firstName.trim() || !form.lastName.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Teacher'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtSchoolTeacherModal;
